// The Ask User communication card from the chat component gallery, shared by
// the message-review and voice-call sections. Exported as composable pieces
// rather than one mega-component because the two variants assemble the same
// parts differently: message review edits a shared template, while voice
// review uses a purpose field and its own actions.
import { X } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { TEMPLATE_VARIABLE_DRAG_TYPE } from '@apps/web/components/shared/messages/variable_text_editor';
import {
    CARD_SURFACES,
    CompactReceipt,
    type ReceiptStatus,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_receipt';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { cn } from '@apps/web/lib/utils';

export type CommunicationTone = 'editing' | 'approved' | 'rejected';

export type CommunicationCardProps = Readonly<{
    readonly tone: CommunicationTone;
    readonly children: ReactNode;
    readonly className?: string;
}>;

export function CommunicationCard({
    tone,
    children,
    className,
}: CommunicationCardProps): ReactNode {
    return (
        <div
            aria-live="polite"
            data-state={tone}
            className={cn(
                'relative w-full overflow-hidden',
                CARD_SURFACES.communication,
                className,
            )}
        >
            {children}
        </div>
    );
}

export type CommunicationFieldsProps = Readonly<{
    readonly children: ReactNode;
}>;

export function CommunicationFields({
    children,
}: CommunicationFieldsProps): ReactNode {
    return (
        <div className="divide-border/60 divide-y px-3.5 pt-1 pb-0.5">
            {children}
        </div>
    );
}

export type CommunicationFieldProps = Readonly<{
    readonly label: string;
    /** Puts the label on the same wrapped row as the content (To: rows). */
    readonly inline?: boolean;
    readonly children: ReactNode;
}>;

export function CommunicationField({
    label,
    inline = false,
    children,
}: CommunicationFieldProps): ReactNode {
    if (inline) {
        return (
            <div className="flex min-w-0 flex-wrap items-center gap-1.5 pt-3 pb-[13px]">
                <span className="text-foreground/80 text-v3-chat-meta font-semibold">
                    {label}
                </span>
                {children}
            </div>
        );
    }
    return (
        <div className="pt-3 pb-[13px]">
            <span className="text-foreground/80 text-v3-chat-meta mb-[7px] block font-semibold">
                {label}
            </span>
            <div className="min-w-0">{children}</div>
        </div>
    );
}

export type RecipientPillProps = Readonly<{
    readonly name: string;
    readonly onRemove?: () => void;
}>;

export function RecipientPill({
    name,
    onRemove,
}: RecipientPillProps): ReactNode {
    return (
        <span className="bg-muted text-foreground/80 text-v3-chat-meta inline-flex items-center gap-[5px] rounded-md px-[7px] py-1 font-semibold">
            <span>
                <ResolvedReferenceText text={name} />
            </span>
            {onRemove !== undefined && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="hover:text-foreground inline-flex size-5 items-center justify-center rounded-md"
                >
                    <span className="sr-only">
                        Remove <ResolvedReferenceText text={name} />
                    </span>
                    <X aria-hidden="true" className="size-3.5" />
                </button>
            )}
        </span>
    );
}

export type VariableButtonProps = Readonly<{
    readonly children: ReactNode;
    readonly onClick: () => void;
    readonly disabled?: boolean;
    /** The token the drop inserts, e.g. `{{first_name}}`. */
    readonly variable: string;
}>;

/**
 * A variable chip from the gallery toolbar. The caller chooses where clicks
 * insert it; dragging carries plain text so the editor can place it at the
 * pointer.
 */
export function VariableButton({
    children,
    variable,
    onClick,
    disabled = false,
}: VariableButtonProps): ReactNode {
    const [dragging, setDragging] = useState(false);
    return (
        <button
            type="button"
            disabled={disabled}
            draggable={!disabled}
            onClick={onClick}
            onDragStart={(event) => {
                event.dataTransfer.effectAllowed = 'copy';
                event.dataTransfer.setData('text/plain', variable);
                // The browser exposes drag types before it exposes the
                // payload, so the editor can snap the preview while hovering.
                const token = variable.match(/^\{([a-z_]+)\}$/)?.[1];
                if (token) {
                    event.dataTransfer.setData(
                        TEMPLATE_VARIABLE_DRAG_TYPE,
                        token,
                    );
                }
                setDragging(true);
            }}
            onDragEnd={() => setDragging(false)}
            className={cn(
                'bg-card text-foreground/80 hover:border-agent-action-border hover:bg-agent-action-wash inline-flex min-h-6 cursor-grab items-center rounded-md border px-1.5 py-[3px] text-v3-chat-meta font-semibold transition-colors duration-[140ms] ease-[cubic-bezier(0.22,1,0.36,1)] active:cursor-grabbing disabled:cursor-default disabled:opacity-[0.42] motion-reduce:transition-none',
                dragging && 'opacity-45',
            )}
        >
            {children}
        </button>
    );
}

export type RestorePersonButtonProps = Readonly<{
    readonly children: ReactNode;
    readonly onClick: () => void;
}>;

/** The bordered pill that brings a removed recipient back. */
export function RestorePersonButton({
    children,
    onClick,
}: RestorePersonButtonProps): ReactNode {
    return (
        <button
            type="button"
            onClick={onClick}
            className="bg-card text-foreground/80 hover:bg-accent text-v3-chat-meta inline-flex min-h-6 items-center rounded-md border px-[7px] py-1 font-medium"
        >
            {children}
        </button>
    );
}

export type MessageEditorProps = Readonly<{
    readonly value: string;
    readonly ariaLabel: string;
    readonly describedBy?: string;
    readonly onChange?: (value: string) => void;
    /** The voice purpose field is shallower than the message editor. */
    readonly variant?: 'message' | 'purpose';
}>;

export function MessageEditor({
    value,
    ariaLabel,
    describedBy,
    onChange,
    variant = 'message',
}: MessageEditorProps): ReactNode {
    return (
        <textarea
            aria-label={ariaLabel}
            aria-describedby={describedBy}
            value={value}
            readOnly={onChange === undefined}
            onChange={(event) => onChange?.(event.currentTarget.value)}
            className={cn(
                'bg-card w-full resize-none rounded-md border px-2.5',
                'py-[9px] text-v3-chat break-words',
                'focus:border-agent-action outline-0',
                variant === 'message' ? 'min-h-[calc(2lh+20px)]' : 'min-h-12',
            )}
        />
    );
}

export type CommunicationActionsProps = Readonly<{
    readonly note?: string;
    readonly children: ReactNode;
}>;

export function CommunicationActions({
    note,
    children,
}: CommunicationActionsProps): ReactNode {
    return (
        <footer className="flex flex-wrap items-center justify-end gap-2 px-3.5 py-3">
            {note !== undefined && (
                <span className="text-muted-foreground text-v3-chat-meta min-w-0 flex-1">
                    <ResolvedReferenceText text={note} />
                </span>
            )}
            {children}
        </footer>
    );
}

export type CardActionButtonProps = Readonly<{
    readonly variant: 'primary' | 'secondary';
    readonly children: ReactNode;
    readonly onClick: () => void;
    readonly disabled?: boolean;
}>;

export function CardActionButton({
    variant,
    children,
    onClick,
    disabled = false,
}: CardActionButtonProps): ReactNode {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={cn(
                'min-h-[31px] rounded-md border px-2.5 py-1.5 text-v3-chat',
                'font-semibold disabled:opacity-[0.42]',
                variant === 'primary'
                    ? 'border-agent-action bg-agent-action hover:border-agent-action-strong hover:bg-agent-action-strong text-white'
                    : 'bg-card text-foreground/80 hover:bg-accent',
            )}
        >
            {children}
        </button>
    );
}

export type CommunicationReceiptProps = Readonly<{
    readonly status?: ReceiptStatus;
    readonly statusLabel?: string;
    readonly tone: 'approved' | 'rejected';
    readonly title: string;
    readonly detail?: string;
    readonly action?: ReactNode;
}>;

export function CommunicationReceipt({
    tone,
    status,
    statusLabel,
    title,
    detail,
    action,
}: CommunicationReceiptProps): ReactNode {
    return (
        <CompactReceipt
            embedded={true}
            surface="communication"
            title={title}
            statusLabel={
                statusLabel ??
                (status === undefined
                    ? tone === 'approved'
                        ? 'Approved'
                        : 'Rejected'
                    : undefined)
            }
            status={status ?? (tone === 'approved' ? 'succeeded' : 'denied')}
            summary={
                detail === undefined ? undefined : (
                    <ResolvedReferenceText text={detail} />
                )
            }
            action={action}
        />
    );
}
